<script setup lang="ts">
import type { IBankingRegionsStory } from './useBankingRegions'
import { useBankingNumber } from './useBankingNumber'

// Hero fact of the month: the capital's share of the country's deposits (#515).
// "compact" is the short phone version shown inside the hero text column.
const props = defineProps<{
  story: IBankingRegionsStory
  compact?: boolean
}>()

const { t } = useI18n()
const { formatNumber } = useBankingNumber()

const share = computed(() => `${formatNumber(props.story.capital.deposits_share, 1)}%`)
const rest = computed(() => `${formatNumber(100 - props.story.capital.deposits_share, 1)}%`)

// The comparison with the leading region is the regions block headline; the hero does not repeat it
const text = computed(() =>
  t('banking_statistics.hero_fact.text', {
    capital_ratio: formatNumber(props.story.capital.credits_per_deposit, 2)
  })
)
</script>

<template>
  <div
    v-if="props.compact"
    data-testid="banking-hero-fact-compact"
    class="flex w-full flex-col gap-1 rounded-xl bg-white p-4 shadow-sm ring-1 ring-blue-100"
  >
    <span class="text-xs font-bold uppercase tracking-wide text-blue-600">
      {{ t('banking_statistics.hero_fact.label') }}
    </span>
    <span class="font-serif text-3xl leading-none text-brand-gray">{{ share }}</span>
    <span class="text-sm text-gray-700">{{ t('banking_statistics.hero_fact.short') }}</span>
    <a href="#regions" class="text-sm font-bold text-blue-600 hover:text-blue-700">
      {{ t('banking_statistics.hero_fact.link') }} ↓
    </a>
  </div>
  <div
    v-else
    data-testid="banking-hero-fact"
    class="relative z-10 mx-6 flex w-full max-w-[21rem] flex-col gap-2.5 rounded-2xl bg-white p-5 shadow-lg shadow-blue-600/10"
  >
    <span class="text-xs font-bold uppercase tracking-wide text-blue-600">
      {{ t('banking_statistics.hero_fact.label') }}
    </span>
    <span class="font-serif text-4xl leading-none text-brand-gray">{{ share }}</span>
    <p class="text-sm leading-5 text-gray-700">{{ text }}</p>
    <svg
      class="h-2.5 w-full overflow-hidden rounded-full"
      viewBox="0 0 100 10"
      preserveAspectRatio="none"
      role="img"
      :aria-label="t('banking_statistics.hero_fact.bar', { share, rest })"
    >
      <rect width="100" height="10" class="fill-blue-100" />
      <rect :width="props.story.capital.deposits_share" height="10" class="fill-blue-600" />
    </svg>
    <div class="flex justify-between text-xs text-gray-500">
      <span>{{ t(`banking_statistics.regions.names.${props.story.capital.key}`) }}</span>
      <span>{{ t('banking_statistics.hero_fact.rest') }}</span>
    </div>
    <a href="#regions" class="text-sm font-bold text-blue-600 hover:text-blue-700">
      {{ t('banking_statistics.hero_fact.link') }} ↓
    </a>
  </div>
</template>
